import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { Paper, PromptConfirmation } from 'ui';
import { withPrompt, PromptProps, SpinLoader } from 'app/components';
import { STATE } from 'app/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { hasTranslationsLoaded, translationChanged } from 'app/infrastructure/translate/selectors';
import { OptionsProps, withOptions } from 'app/components/options-handler';
import { externalUrlAdminTranslation } from '../constants';
import { ExternalUrlAdminState } from '../types-store';
import { ExternalUrlFormSections } from './components/external-url-form-sections';
import { actionCreators } from './actions';
import { ExternalUrlFormSelectorProps, selectors } from './selectors';
import { ExternalUrlItem } from './types-store';
import { getExternalUrlAdminAccess } from 'app/area/external-urls-administration/get-access';
import { scrollToTop } from 'app/infrastructure/platform/routing';
import { ExternalUrlActionType } from 'app/area/external-urls-administration/form/types';

type ExternalUrlFormProps = DefaultContainerProps
  & OptionsProps
  & PromptProps
  & ExternalUrlAdminState
  & ExternalUrlFormSelectorProps
  & typeof actionCreators;

interface ExternalUrlFormState {
  showTranslations: boolean;
}

class ExternalUrlFormComponent extends React.PureComponent<ExternalUrlFormProps, ExternalUrlFormState> {
  public static statePath: string = STATE.externalUrlAdministration;

  public state: Readonly<ExternalUrlFormState> = {
    showTranslations: false
  };

  public componentDidMount() {
    scrollToTop();
    this.props.loadLanguageOptions();

    if (hasTranslationsLoaded(this.props.translation)) {
      this.initForm();
    }
  }

  public componentDidUpdate(prevProps: ExternalUrlFormProps) {
    if (this.props.id !== prevProps.id ||
      translationChanged(this.props.translation, prevProps.translation)) {
      this.initForm();
    }

    if (prevProps?.form?.validation?.language !== this.props?.form?.validation?.language) {
      this.updateTranslationsToggle();
    }
  }

  public componentWillUnmount() {
    this.props.clearForm();
  }

  private initForm = () => {
    const { id, sharedState, createExternalUrl, loadExternalUrl: loadExternalUrl, displayError, translation } = this.props;

    const externalUrlAccess = getExternalUrlAdminAccess(sharedState);
    if (!externalUrlAccess) {
      displayError(translation.no_access_to_external_urls);
      navigate.toStartupView(true);
      return;
    }

    if (isDefinedIncludingZero(id)) {
      loadExternalUrl(id);
    } else {
      createExternalUrl();
    }
  };

  private switchTranslationsToggle = (showTranslations: boolean) => this.setState({ showTranslations });

  private updateTranslationsToggle = () => {
    if (!!this.props?.form?.validation?.language) {
      this.switchTranslationsToggle(true);
    }
  };

  private saveExternalUrl = (externalUrl: ExternalUrlItem) => {
    const { params: { actionType } = {}, saveExternalUrl: saveExternalUrl, translation } = this.props;

    const actionTypeId = ExternalUrlActionType[actionType];
    const successMessage = (isDefinedIncludingZero(externalUrl?.id)
      && actionTypeId === ExternalUrlActionType.edit)
      ? translation.external_url_updated
      : translation.external_url_created;

    saveExternalUrl(actionTypeId, externalUrl, successMessage, translation.external_url_validation_error);
  };

  private deleteExternalUrl = () => {
    const { id, form: { externalUrlItem: { language: externalUrlLanguage = [] } = {} } = {},
      sharedState: { language: { value: languageValue } = {} } = {},
      translation, deleteExternalUrl: deleteExternalUrl } = this.props;

    deleteExternalUrl(
      id,
      externalUrlLanguage.find(option => option.value === languageValue)?.description,
      translation.external_url_deleted
    );
  };

  public render() {
    const { section, sharedState, form, translation,
      options, shouldPrompt,
      params: { actionType },
      loadOptions, updateExcludedFields,
      changeLanguage, copyLanguageToAll, changeLongDescription, copyLongDescriptionToAll,
      updateItem, updateValidation, resetValidation, displayError
    } = this.props;
    const mainLanguage = sharedState?.language?.value ?? undefined;
    const locale = sharedState?.locale || '';
    const translationAdminAccess = sharedState?.access?.adminPanel?.translation?.administrationCentral;

    return <div className="entity-view">
      <Paper componentId="external-url-paper" className={undefined}>
        {!!this.props.shouldDisplayModal &&
          <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel} />}

        {!form.languageOptions || !form.externalUrlItem || form.fetching
          ? <SpinLoader />
          : <ExternalUrlFormSections
            actionType={ExternalUrlActionType[actionType]}
            formState={form}
            shouldPrompt={shouldPrompt}
            mainLanguage={mainLanguage}
            saveExternalUrl={this.saveExternalUrl}
            deleteExternalUrl={this.deleteExternalUrl}
            updateItem={updateItem}
            changeLanguage={changeLanguage}
            copyLanguageToAll={copyLanguageToAll}
            changeLongDescription={changeLongDescription}
            copyLongDescriptionToAll={copyLongDescriptionToAll}
            options={options}
            loadOptions={loadOptions}
            updateExcludedFields={updateExcludedFields}
            updateValidation={updateValidation}
            resetValidation={resetValidation}
            displayError={displayError}
            section={section}
            locale={locale}
            translation={translation}
            translationAdminAccess={translationAdminAccess}
            showTranslations={this.state.showTranslations}
            switchTranslationsToggle={this.switchTranslationsToggle} />}
      </Paper>
    </div>;
  }
}

const container = containerFactory(
  withPrompt(withOptions(ExternalUrlFormComponent)),
  actionCreators, selectors, externalUrlAdminTranslation
);

export { container as ExternalUrlForm };
